import React, { useEffect, useState } from 'react';
import {
  Card,
  Statistic,
  Button,
  Table,
  Tag,
  Progress,
  Alert,
  Space,
  Typography,
  message,
  Row,
  Col,
} from 'antd';
import {
  CopyOutlined,
  UserAddOutlined,
  GiftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { getInviteStats, getInviteRecords, type InviteStats, type InviteRecord } from '../services/invite';
import { useTranslation } from '../hooks/useTranslation';

const { Title, Text } = Typography;

const Invite: React.FC = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState<InviteStats | null>(null);
  const [records, setRecords] = useState<InviteRecord[]>([]);
  const [loading, setLoading] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsData, recordsData] = await Promise.all([
        getInviteStats(),
        getInviteRecords(),
      ]);
      setStats(statsData);
      setRecords(recordsData);
    } catch (error: any) {
      message.error(error.message || t('invite.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const copyInviteCode = () => {
    if (stats?.inviteCode) {
      navigator.clipboard.writeText(stats.inviteCode);
      message.success(t('invite.codeCopied'));
    }
  };

  const copyInviteLink = () => {
    if (stats?.inviteCode) {
      // 使用 hash 路由格式：域名/#/invite-welcome?invite=INV-XXXX
      const baseUrl = window.location.origin + window.location.pathname;
      const inviteLink = `${baseUrl}#/invite-welcome?invite=${stats.inviteCode}`;
      navigator.clipboard.writeText(inviteLink);
      message.success(t('invite.linkCopied'));
    }
  };

  const getInviteLink = () => {
    if (stats?.inviteCode) {
      // 使用 hash 路由格式：域名/#/invite-welcome?invite=INV-XXXX
      const baseUrl = window.location.origin + window.location.pathname;
      return `${baseUrl}#/invite-welcome?invite=${stats.inviteCode}`;
    }
    return '';
  };


  const columns: ColumnsType<InviteRecord> = [
    {
      title: t('invite.invitee'),
      dataIndex: 'inviteeUsername',
      key: 'inviteeUsername',
      render: (text: string) => text || '-',
    },
    {
      title: t('invite.firstLoginTime'),
      dataIndex: 'inviteeFirstLoginTime',
      key: 'inviteeFirstLoginTime',
      render: (time: number) => {
        if (!time) return '-';
        return new Date(time).toLocaleString('zh-CN');
      },
    },
    {
      title: t('invite.rewardStatus'),
      dataIndex: 'rewardGiven',
      key: 'rewardGiven',
      render: (given: boolean, record: InviteRecord) => {
        if (!record.inviteeUsername) {
          return <Tag color="default">{t('invite.notUsed')}</Tag>;
        }
        if (given) {
          return (
            <Tag color="success" icon={<CheckCircleOutlined />}>
              {t('invite.rewardGiven')}
            </Tag>
          );
        }
        return (
          <Tag color="warning" icon={<CloseCircleOutlined />}>
            {t('invite.rewardNotGiven')}
          </Tag>
        );
      },
    },
    {
      title: t('invite.rewardTime'),
      dataIndex: 'rewardGivenTime',
      key: 'rewardGivenTime',
      render: (time: number) => {
        if (!time) return '-';
        return new Date(time).toLocaleString('zh-CN');
      },
    },
    {
      title: t('invite.createTime'),
      dataIndex: 'createTime',
      key: 'createTime',
      render: (time: number) => {
        if (!time) return '-';
        return new Date(time).toLocaleString('zh-CN');
      },
    },
  ];

  if (!stats) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Text>{t('common.loading')}</Text>
      </div>
    );
  }

  const progressPercent = stats.rewardLimit > 0 
    ? (stats.rewardCount / stats.rewardLimit) * 100 
    : 0;

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* 统计信息卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title={t('invite.rewardEarned')}
              value={stats.rewardCount}
              suffix={`/ ${stats.rewardLimit}`}
              prefix={<GiftOutlined />}
              valueStyle={{ color: stats.reachedRewardLimit ? '#ff4d4f' : '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title={t('invite.totalInvites')}
              value={stats.totalInviteCount}
              prefix={<UserAddOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Card>
            <Statistic
              title={t('invite.remainingRewards')}
              value={stats.remainingRewards}
              valueStyle={{ color: stats.remainingRewards > 0 ? '#3f8600' : '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 奖励进度 */}
      <Card style={{ marginBottom: '24px' }}>
        <div style={{ marginBottom: '16px' }}>
          <Text strong>{t('invite.rewardProgress')}:</Text>
          <Text style={{ marginLeft: '8px' }}>
            {stats.rewardCount} / {stats.rewardLimit} {t('invite.times')}
          </Text>
        </div>
        <Progress
          percent={progressPercent}
          status={stats.reachedRewardLimit ? 'exception' : 'active'}
          strokeColor={stats.reachedRewardLimit ? '#ff4d4f' : '#52c41a'}
        />
        {stats.reachedRewardLimit ? (
          <Alert
            message={t('invite.rewardLimitReached')}
            type="warning"
            style={{ marginTop: '16px' }}
          />
        ) : (
          <Alert
            message={t('invite.rewardProgressMessage', { count: stats.rewardCount, remaining: stats.remainingRewards })}
            type="info"
            style={{ marginTop: '16px' }}
          />
        )}
      </Card>

      {/* 邀请码和邀请链接 */}
      <Card style={{ marginBottom: '24px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Title level={4}>{t('invite.myInviteCode')}</Title>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
              <Text
                code
                style={{
                  fontSize: '20px',
                  padding: '8px 16px',
                  backgroundColor: '#f0f0f0',
                  borderRadius: '4px',
                }}
              >
                {stats.inviteCode}
              </Text>
              <Button
                type="primary"
                icon={<CopyOutlined />}
                onClick={copyInviteCode}
              >
                {t('invite.copyCode')}
              </Button>
            </div>
          </div>

          <div>
            <Title level={4}>{t('invite.inviteLink')}</Title>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
              <Text
                style={{
                  fontSize: '14px',
                  padding: '8px 16px',
                  backgroundColor: '#f0f0f0',
                  borderRadius: '4px',
                  flex: 1,
                  wordBreak: 'break-all',
                }}
              >
                {getInviteLink()}
              </Text>
              <Button
                type="primary"
                icon={<CopyOutlined />}
                onClick={copyInviteLink}
              >
                {t('invite.copyLink')}
              </Button>
            </div>
          </div>
        </Space>
      </Card>

      {/* 邀请记录 */}
      <Card>
        <Title level={4} style={{ marginBottom: '16px' }}>{t('invite.inviteRecords')}</Title>
        <Table
          columns={columns}
          dataSource={records}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => t('invite.totalRecords', { total }),
          }}
        />
      </Card>
    </div>
  );
};

export default Invite;
